(() => {
  const CODE = 'PRIME10';
  const STATE_KEY = 'verymill_prime10_state';
  const PENDING_KEY = 'verymill_prime10_pending_once';
  const root = () => (window.Shopify?.routes?.root || '/');
  let applying = false;

  const buttons = () => Array.from(document.querySelectorAll('[data-vm-prime-code]'));

  const getState = () => {
    try {
      return localStorage.getItem(STATE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const setState = (value) => {
    try {
      if (value) localStorage.setItem(STATE_KEY, value);
      else localStorage.removeItem(STATE_KEY);
    } catch (_) {}
  };

  const setPendingOnce = (value) => {
    try {
      if (value) sessionStorage.setItem(PENDING_KEY, '1');
      else sessionStorage.removeItem(PENDING_KEY);
    } catch (_) {}
  };

  const hasPendingOnce = () => {
    try {
      return sessionStorage.getItem(PENDING_KEY) === '1';
    } catch (_) {
      return false;
    }
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-vm-prime-toast]');
    if (!toast) return;
    toast.textContent = message || `${CODE} copied & applied`;
    toast.classList.add('is-show');
    clearTimeout(window.__vmPrimeToastTimer);
    window.__vmPrimeToastTimer = setTimeout(() => toast.classList.remove('is-show'), 2800);
  };

  const setAppliedUI = (isApplied) => {
    buttons().forEach((button) => {
      button.classList.toggle('is-applied', Boolean(isApplied));
      button.setAttribute(
        'aria-label',
        isApplied ? `Discount code ${CODE} applied` : `Copy and apply discount code ${CODE}`
      );
    });
  };

  const copyCode = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(CODE);
        return true;
      }
    } catch (_) {}

    try {
      const textarea = document.createElement('textarea');
      textarea.value = CODE;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      return copied;
    } catch (_) {
      return false;
    }
  };

  const refreshVisibleCartUI = async () => {
    const drawer = document.querySelector('#MinimogCartDrawer');
    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
      try {
        await drawer.onCartDrawerUpdate(true);
      } catch (_) {}
    }

    if (/\/cart\/?$/.test(window.location.pathname)) {
      window.location.reload();
    }
  };

  const applyCodeOnce = async ({ copy = false, refresh = true, quiet = false } = {}) => {
    if (applying) return null;
    applying = true;

    let copied = false;
    if (copy) copied = await copyCode();

    try {
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ discount: CODE })
      });

      const cart = await response.json();
      if (!response.ok || cart?.status) {
        throw new Error(cart?.description || cart?.message || 'Unable to apply discount code.');
      }

      setState('applied');
      setAppliedUI(true);

      // If the customer clicked while cart is empty, Shopify may not calculate
      // the discount until the first item exists. Allow exactly one retry after
      // the next add-to-cart action, then clear the pending flag.
      setPendingOnce(Number(cart?.item_count || 0) === 0);

      if (!quiet) {
        showToast(copied ? `${CODE} copied & applied` : `${CODE} applied`);
      }

      if (refresh) await refreshVisibleCartUI();

      document.dispatchEvent(new CustomEvent('vm:prime-discount-applied', {
        detail: { code: CODE, cart, copied }
      }));
      return cart;
    } catch (error) {
      // The click is still the only action that arms the one-time retry.
      // Do not start any page-load or recurring apply behavior.
      setState('applied');
      setAppliedUI(true);
      setPendingOnce(true);
      if (!quiet) {
        showToast(copied ? `${CODE} copied — will apply after your first item is added` : `${CODE} ready`);
      }
      return null;
    } finally {
      applying = false;
    }
  };

  const retryOnceAfterAdd = () => {
    if (!hasPendingOnce()) return;

    // Consume the flag before scheduling so multiple cart events cannot queue
    // repeated requests.
    setPendingOnce(false);
    window.setTimeout(() => {
      applyCodeOnce({ copy: false, refresh: true, quiet: true });
    }, 650);
  };

  document.addEventListener('click', (event) => {
    const primeButton = event.target.closest('[data-vm-prime-code]');
    if (primeButton) {
      event.preventDefault();
      applyCodeOnce({ copy: true, refresh: true, quiet: false });
      return;
    }

    const addButton = event.target.closest('[name="add"], [data-add-to-cart], .m-add-to-cart');
    if (addButton && hasPendingOnce()) {
      retryOnceAfterAdd();
    }
  });

  document.addEventListener('submit', (event) => {
    if (!hasPendingOnce()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (action.includes('/cart/add')) retryOnceAfterAdd();
  }, true);

  // Cart Drawer removal explicitly resets the promo button. This does not
  // trigger any re-apply request.
  document.addEventListener('vm:discount-removed', () => {
    setState('');
    setPendingOnce(false);
    setAppliedUI(false);
  });

  // Another tab can remove the state; only update UI, never call Shopify.
  window.addEventListener('storage', (event) => {
    if (event.key !== STATE_KEY) return;
    setAppliedUI((event.newValue || '') === 'applied');
  });

  const init = () => {
    // UI state only. No network request and no automatic apply on page load.
    setAppliedUI(getState() === 'applied');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
